// src/components/HermodsPath/InspirationPanel.js
// Collapsible content idea bank panel for Hermod's Path compose tab

import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, ArrowRight } from 'lucide-react';
import { getIdeasByType, getIdeaContentTypes } from '../../data/contentIdeas';

export default function InspirationPanel({ onSelectIdea, activeContentType }) {
  const [expanded, setExpanded] = useState(false);
  const [filterType, setFilterType] = useState(null);

  // Auto-sync filter to compose panel's content type (unless user overrides)
  const effectiveFilter = filterType ?? (activeContentType !== 'General' ? activeContentType : null);

  const ideas = useMemo(
    () => getIdeasByType(effectiveFilter),
    [effectiveFilter]
  );

  const ideaContentTypes = useMemo(() => getIdeaContentTypes(), []);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl p-4 text-left transition flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-amber-500" />
          <div>
            <h3 className="text-sm font-bold text-slate-800">Need inspiration?</h3>
            <p className="text-xs text-slate-500 mt-0.5">{ideas.length} post ideas to get you started</p>
          </div>
        </div>
        <ChevronDown size={16} className="text-slate-400" />
      </button>
    );
  }

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(false)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-amber-100 transition"
      >
        <div className="flex items-center gap-2">
          <Lightbulb size={16} className="text-amber-500" />
          <h3 className="text-sm font-bold text-slate-800">Need inspiration?</h3>
        </div>
        <ChevronUp size={16} className="text-slate-400" />
      </button>

      {/* Content type filter pills */}
      <div className="px-4 pb-2 flex gap-1.5 overflow-x-auto">
        <button
          onClick={() => setFilterType(null)}
          className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${
            effectiveFilter === null
              ? 'bg-amber-500 text-white'
              : 'bg-white text-slate-500 hover:text-slate-700 border border-slate-200'
          }`}
        >
          All
        </button>
        {ideaContentTypes.map(type => (
          <button
            key={type}
            onClick={() => setFilterType(type)}
            className={`px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition ${
              effectiveFilter === type
                ? 'bg-amber-500 text-white'
                : 'bg-white text-slate-500 hover:text-slate-700 border border-slate-200'
            }`}
          >
            {type}
          </button>
        ))}
      </div>

      {/* Ideas list */}
      <div className="px-4 pb-4 max-h-64 overflow-y-auto space-y-2">
        {ideas.map(idea => (
          <button
            key={idea.id}
            onClick={() => {
              onSelectIdea(idea);
              setExpanded(false);
            }}
            className="w-full text-left bg-white rounded-lg p-3 border border-slate-200 hover:border-amber-300 hover:shadow-sm transition group"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 group-hover:text-amber-700 transition">
                  {idea.title}
                </p>
                <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{idea.description}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                    {idea.frequency}
                  </span>
                  <span className="text-[10px] text-slate-400">
                    {idea.suggestedPlatforms.length} platform{idea.suggestedPlatforms.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              <ArrowRight size={14} className="text-slate-300 group-hover:text-amber-500 transition mt-1 shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
